import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MicrophoneIcon,
  VideoCameraIcon,
  SpeakerWaveIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { isProdHost } from '../../lib/isProdHost';
import { useCandidateT } from '../../i18n/candidate';

/**
 * DeviceCheckModal
 * 
 * A pre-interview modal that tests microphone (and camera for video interviews)
 * before allowing the candidate to proceed. Handles all edge cases:
 * - Permission denied / dismissed
 * - No device found
 * - Device not working (no audio detected)
 * - Browser not supporting getUserMedia
 * - Device disconnected mid-test
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void (cancel / go back)
 * - onProceed: () => void (all checks passed, start interview)
 * - interviewType: 'voice' | 'video'
 */
const DeviceCheckModal = ({ isOpen, onClose, onProceed, interviewType = 'voice' }) => {
  const isVideo = interviewType === 'video';
  const t = useCandidateT();
  // The test callbacks below are memoised once, so the error text they build reads the
  // translator through a ref to always use the current language.
  const tRef = useRef(t);
  tRef.current = t;
  const tr = (text, vars) => tRef.current(text, vars);

  // --- State ---
  // 'idle' | 'testing' | 'passed' | 'failed'
  const [micStatus, setMicStatus] = useState('idle');
  const [cameraStatus, setCameraStatus] = useState('idle');
  const [micError, setMicError] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [peakDetected, setPeakDetected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [browserSupported, setBrowserSupported] = useState(true);

  // Camera preview
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  // Audio refs
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const playbackAudioRef = useRef(null);

  // --- Browser support check ---
  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setBrowserSupported(false);
    }
  }, []);

  // --- Cleanup on close ---
  const cleanupAll = useCallback(() => {
    // Stop mic stream
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    // Stop camera stream
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    }
    // Stop audio context
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    // Stop playback
    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause();
      playbackAudioRef.current = null;
    }
    // Stop media recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
      mediaRecorderRef.current = null;
    }
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMicStatus('idle');
      setCameraStatus('idle');
      setMicError(null);
      setCameraError(null);
      setAudioLevel(0);
      setPeakDetected(false);
      setIsRecording(false);
      setIsPlaying(false);
      setRecordedBlob(null);
      setCountdownSeconds(0);
    } else {
      cleanupAll();
    }
  }, [isOpen, cleanupAll]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupAll();
  }, [cleanupAll]);

  // --- Device & Browser detection ---
  const detectPlatform = useCallback(() => {
    const ua = navigator.userAgent || '';
    const vendor = navigator.vendor || '';
    const platform = navigator.platform || '';

    // --- OS / Device ---
    let os = 'unknown';
    let device = 'desktop';
    if (/iPhone/i.test(ua)) { os = 'ios'; device = 'iphone'; }
    else if (/iPad/i.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) { os = 'ios'; device = 'ipad'; }
    else if (/Android/i.test(ua)) { os = 'android'; device = 'android'; }
    else if (/Mac/i.test(platform)) { os = 'mac'; device = 'desktop'; }
    else if (/Win/i.test(platform)) { os = 'windows'; device = 'desktop'; }
    else if (/Linux/i.test(platform)) { os = 'linux'; device = 'desktop'; }
    else if (/CrOS/i.test(ua)) { os = 'chromeos'; device = 'desktop'; }

    // --- Browser ---
    let browser = 'unknown';
    // Order matters — check more specific UAs first
    if (/Brave/i.test(ua) || (navigator.brave && typeof navigator.brave.isBrave === 'function')) {
      browser = 'brave';
    } else if (/OPR|Opera/i.test(ua)) {
      browser = 'opera';
    } else if (/Edg/i.test(ua)) {
      browser = 'edge';
    } else if (/SamsungBrowser/i.test(ua)) {
      browser = 'samsung';
    } else if (/Arc/i.test(ua)) {
      browser = 'arc';
    } else if (/Firefox|FxiOS/i.test(ua)) {
      browser = 'firefox';
    } else if (/CriOS/i.test(ua)) {
      browser = 'chrome'; // Chrome on iOS
    } else if (/Chrome/i.test(ua) && /Google Inc/i.test(vendor)) {
      browser = 'chrome';
    } else if (/Safari/i.test(ua) && /Apple/i.test(vendor)) {
      browser = 'safari';
    }

    return { os, device, browser };
  }, []);

  // --- Get browser display name ---
  const getBrowserDisplayName = (browser) => {
    const names = {
      chrome: 'Google Chrome',
      firefox: 'Mozilla Firefox',
      safari: 'Safari',
      edge: 'Microsoft Edge',
      brave: 'Brave',
      opera: 'Opera',
      samsung: 'Samsung Internet',
      arc: 'Arc',
    };
    return names[browser] || tr('your browser');
  };

  // --- Get device display name ---
  const getDeviceDisplayName = (device, os) => {
    const names = {
      iphone: 'iPhone',
      ipad: 'iPad',
      android: 'Android',
      desktop: os === 'mac' ? 'Mac' : os === 'windows' ? 'Windows' : os === 'linux' ? 'Linux' : os === 'chromeos' ? 'Chromebook' : tr('Desktop'),
    };
    return names[device] || tr('your device');
  };

  // --- Platform-specific fix instructions ---
  const getPermissionFixSteps = (deviceType, platform) => {
    const { os, device, browser } = platform;
    // Placeholders for the translated steps (English output is unchanged).
    const v = {
      device: tr(deviceType),
      deviceLower: tr(deviceType).toLowerCase(),
      model: device === 'iphone' ? 'iPhone' : 'iPad',
      browser: getBrowserDisplayName(browser),
    };

    // ========= iOS (iPhone / iPad) =========
    if (os === 'ios') {
      if (browser === 'safari') {
        return [
          tr('Open the Settings app on your {model}', v),
          tr('Scroll down and tap "Safari"'),
          tr('Tap "{device}" and select "Allow"', v),
          tr('Come back to this page and tap "Try again"'),
        ];
      }
      // Chrome / Firefox / other browsers on iOS
      return [
        tr('Open the Settings app on your {model}', v),
        tr('Scroll down and tap "{browser}"', v),
        tr('Make sure "{device}" is set to "Allow"', v),
        tr('Come back to this page and tap "Try again"'),
      ];
    }

    // ========= Android =========
    if (os === 'android') {
      if (browser === 'chrome') {
        return [
          tr('Tap the 🔒 lock icon in the address bar at the top'),
          tr('Tap "Permissions" or "Site settings"'),
          tr('Find "{device}" and change it to "Allow"', v),
          tr('The page will refresh automatically'),
        ];
      }
      if (browser === 'samsung') {
        return [
          tr('Tap the ☰ menu icon (three lines) at the bottom right'),
          tr('Tap "Settings" → "Sites and downloads"'),
          tr('Tap "{device}" and set it to "Allow"', v),
          tr('Go back to this page and tap "Try again"'),
        ];
      }
      if (browser === 'firefox') {
        return [
          tr('Tap the 🔒 lock icon in the address bar'),
          tr('Tap "Edit Site Permissions"'),
          tr('Toggle "{device}" to allowed', v),
          tr('Tap "Try again" below'),
        ];
      }
      if (browser === 'brave') {
        return [
          tr('Tap the Brave shield icon (lion) in the address bar'),
          tr('Tap "Advanced controls" or "Site settings"'),
          tr('Find "{device}" and set to "Allow"', v),
          tr('Refresh this page'),
        ];
      }
      if (browser === 'opera') {
        return [
          tr('Tap the address bar, then the 🔒 lock icon'),
          tr('Tap "Site settings"'),
          tr('Set "{device}" to "Allow"', v),
          tr('Refresh this page'),
        ];
      }
      // Generic Android
      return [
        tr('Tap the lock or info icon in the address bar'),
        tr('Go to "Site settings" or "Permissions"'),
        tr('Allow "{device}" access', v),
        tr('Refresh this page'),
      ];
    }

    // ========= Desktop: macOS =========
    if (os === 'mac') {
      if (browser === 'safari') {
        return [
          tr('Click "Safari" in the top menu bar → "Settings" (or "Preferences")'),
          tr('Go to the "Websites" tab'),
          tr('Click "{device}" in the left sidebar', v),
          tr('Find this website and change permission to "Allow"'),
          tr('Close Settings and refresh this page'),
        ];
      }
      if (browser === 'chrome') {
        return [
          tr('Click the 🔒 lock icon in the address bar (top left)'),
          tr('Click "Site settings"'),
          tr('Find "{device}" and change from "Block" to "Allow"', v),
          tr('Close the tab and refresh this page'),
          tr('Also check: System Settings → Privacy & Security → {device} → Enable for Chrome', v),
        ];
      }
      if (browser === 'firefox') {
        return [
          tr('Click the 🔒 lock icon in the address bar'),
          tr('Click the arrow next to "Connection secure"'),
          tr('Click "More Information" → "Permissions" tab'),
          tr('Find "{device}" and uncheck "Block" / check "Allow"', v),
          tr('Refresh this page'),
        ];
      }
      if (browser === 'edge') {
        return [
          tr('Click the 🔒 lock icon in the address bar'),
          tr('Click "Site permissions"'),
          tr('Find "{device}" and change to "Allow"', v),
          tr('Refresh this page'),
          tr('Also check: System Settings → Privacy & Security → {device} → Enable for Edge', v),
        ];
      }
      if (browser === 'brave') {
        return [
          tr('Click the 🔒 lock icon in the address bar'),
          tr('Click "Site settings"'),
          tr('Find "{device}" and set to "Allow"', v),
          tr('Refresh this page'),
        ];
      }
      if (browser === 'arc') {
        return [
          tr('Click the site icon in the address bar'),
          tr('Click "More Permissions"'),
          tr('Toggle "{device}" to "Allow"', v),
          tr('Refresh this page'),
        ];
      }
      // Generic Mac
      return [
        tr('Click the 🔒 lock icon in the address bar'),
        tr('Go to "Site settings" or "Permissions"'),
        tr('Change "{device}" from "Block" to "Allow"', v),
        tr('Also check: System Settings → Privacy & Security → {device}', v),
        tr('Refresh this page'),
      ];
    }

    // ========= Desktop: Windows =========
    if (os === 'windows') {
      if (browser === 'chrome') {
        return [
          tr('Click the 🔒 lock icon in the address bar (top left of the URL)'),
          tr('Click "Site settings"'),
          tr('Find "{device}" and change from "Block" to "Allow"', v),
          tr('Close the Settings tab and refresh this page'),
        ];
      }
      if (browser === 'firefox') {
        return [
          tr('Click the 🔒 lock icon in the address bar'),
          tr('Click the "X" next to the blocked permission, or'),
          tr('Click "More Information" → "Permissions" tab'),
          tr('Uncheck "Block" next to "{device}"', v),
          tr('Refresh this page'),
        ];
      }
      if (browser === 'edge') {
        return [
          tr('Click the 🔒 lock icon in the address bar'),
          tr('Click "Site permissions"'),
          tr('Find "{device}" and change to "Allow"', v),
          tr('Refresh this page'),
        ];
      }
      if (browser === 'brave') {
        return [
          tr('Click the 🔒 lock icon in the address bar'),
          tr('Click "Site settings"'),
          tr('Find "{device}" and set to "Allow"', v),
          tr('Refresh this page'),
        ];
      }
      if (browser === 'opera') {
        return [
          tr('Click the 🔒 lock icon in the address bar'),
          tr('Click "Site settings"'),
          tr('Find "{device}" and change to "Allow"', v),
          tr('Refresh this page'),
        ];
      }
      // Generic Windows
      return [
        tr('Click the 🔒 lock icon in the address bar'),
        tr('Find "{device}" permission and set to "Allow"', v),
        tr('Refresh this page'),
        tr('Also check: Windows Settings → Privacy → {device}', v),
      ];
    }

    // ========= Linux / ChromeOS / Fallback =========
    if (browser === 'chrome') {
      return [
        tr('Click the 🔒 lock icon in the address bar'),
        tr('Click "Site settings"'),
        tr('Change "{device}" to "Allow"', v),
        tr('Refresh this page'),
      ];
    }
    if (browser === 'firefox') {
      return [
        tr('Click the 🔒 lock icon in the address bar'),
        tr('Find the {deviceLower} permission and click "Allow"', v),
        tr('Refresh this page'),
      ];
    }
    // Ultimate fallback
    return [
      tr('Look for a 🔒 lock or camera/microphone icon in the address bar'),
      tr('Find the {deviceLower} permission setting', v),
      tr('Change it from "Block" to "Allow"'),
      tr('Refresh this page and try again'),
    ];
  };

  // --- Permission error decoder (with platform-specific instructions) ---
  const decodeMediaError = (err, deviceType) => {
    const name = err?.name || '';
    const message = err?.message || '';
    const platform = detectPlatform();
    const v = { device: tr(deviceType), deviceLower: tr(deviceType).toLowerCase() };

    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return {
        title: tr('{device} permission denied', v),
        detail: tr('You blocked {deviceLower} access. Follow the steps below for your device to fix it.', v),
        canRetry: false,
        platform,
        fixSteps: getPermissionFixSteps(deviceType, platform),
      };
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return {
        title: tr('No {deviceLower} found', v),
        detail: platform.device === 'android' || platform.os === 'ios'
          ? tr("We couldn't detect a {deviceLower}. Make sure your device's {deviceLower} isn't disabled in system settings.", v)
          : tr("We couldn't detect a {deviceLower} connected to your computer. Please plug one in and try again.", v),
        canRetry: true,
        platform,
      };
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      return {
        title: tr('{device} is in use', v),
        detail: tr('Your {deviceLower} is being used by another app (Zoom, Teams, Meet, etc.). Close those apps and try again.', v),
        canRetry: true,
        platform,
      };
    }
    if (name === 'OverconstrainedError') {
      return {
        title: tr('{device} not compatible', v),
        detail: tr("The selected {deviceLower} doesn't meet the requirements. Try a different device.", v),
        canRetry: true,
        platform,
      };
    }
    if (name === 'AbortError') {
      return {
        title: tr('{device} access cancelled', v),
        detail: tr('The permission popup was dismissed. Please try again and tap "Allow" when your browser asks.'),
        canRetry: true,
        platform,
      };
    }
    // Fallback
    return {
      title: tr('{device} error', v),
      detail: message || tr('Something went wrong accessing your {deviceLower}. Please try again.', v),
      canRetry: true,
      platform,
    };
  };

  // --- Test microphone ---
  const testMicrophone = useCallback(async () => {
    setMicStatus('testing');
    setMicError(null);
    setAudioLevel(0);
    setPeakDetected(false);
    setIsRecording(false);
    setIsPlaying(false);
    setRecordedBlob(null);

    // Stop previous streams
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      // Set up audio analysis
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      analyser.fftSize = 256;
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Listen for device disconnection
      stream.getAudioTracks().forEach((track) => {
        track.onended = () => {
          setMicStatus('failed');
          setMicError({
            title: tr('Microphone disconnected'),
            detail: tr('Your microphone was disconnected during the test. Please reconnect it and try again.'),
            canRetry: true,
          });
        };
      });

      // Start real-time level monitoring
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let peakFound = false;
      const startTime = Date.now();

      const updateLevel = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const normalizedLevel = average / 255;
        setAudioLevel(normalizedLevel);

        if (normalizedLevel > 0.05 && !peakFound) {
          peakFound = true;
          setPeakDetected(true);
        }

        // After 4 seconds, evaluate
        if (Date.now() - startTime > 4000) {
          if (peakFound) {
            // Now do a quick record + playback test
            startRecordingTest(stream);
          } else {
            setMicStatus('failed');
            setMicError({
              title: tr('No audio detected'),
              detail: tr("We couldn't pick up any sound. Please speak or make a noise, check your microphone is not muted, and try again."),
              canRetry: true,
            });
            // cleanup
            stream.getTracks().forEach((t) => t.stop());
            audioContext.close().catch(() => {});
          }
          return; // stop the loop
        }

        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
      setCountdownSeconds(4);
      // Countdown timer
      const countdownInterval = setInterval(() => {
        setCountdownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err) {
      console.error('Microphone test error:', err);
      setMicStatus('failed');
      setMicError(decodeMediaError(err, 'Microphone'));
    }
  }, []);

  // --- Record + playback test ---
  const startRecordingTest = useCallback((stream) => {
    setIsRecording(true);
    const chunks = [];

    try {
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setIsRecording(false);
        setIsPlaying(true);

        // Play back
        const audioUrl = URL.createObjectURL(blob);
        const audio = new Audio(audioUrl);
        playbackAudioRef.current = audio;

        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          setIsPlaying(false);
          setMicStatus('passed');
          // Cleanup mic stream
          if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach((t) => t.stop());
            micStreamRef.current = null;
          }
          if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
          }
        };

        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          setIsPlaying(false);
          // Still pass — playback isn't critical, mic works
          setMicStatus('passed');
          if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach((t) => t.stop());
            micStreamRef.current = null;
          }
        };

        audio.play().catch(() => {
          // Autoplay blocked — still pass
          setIsPlaying(false);
          setMicStatus('passed');
        });
      };

      mediaRecorder.onerror = () => {
        setIsRecording(false);
        // Recording failed but mic works — still pass
        setMicStatus('passed');
      };

      mediaRecorder.start();
      // Record for 2 seconds
      setTimeout(() => {
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
      }, 2000);
    } catch (err) {
      console.error('Recording test error:', err);
      // MediaRecorder not supported — mic still works
      setIsRecording(false);
      setMicStatus('passed');
    }
  }, []);

  // --- Test camera ---
  const testCamera = useCallback(async () => {
    setCameraStatus('testing');
    setCameraError(null);

    // Stop existing camera stream
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      cameraStreamRef.current = stream;

      // Listen for device disconnection
      stream.getVideoTracks().forEach((track) => {
        track.onended = () => {
          setCameraStatus('failed');
          setCameraError({
            title: tr('Camera disconnected'),
            detail: tr('Your camera was disconnected during the test. Please reconnect it and try again.'),
            canRetry: true,
          });
        };
      });

      // Attach to video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Quick validation — wait a moment then verify track is live
      setTimeout(() => {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack && videoTrack.readyState === 'live') {
          setCameraStatus('passed');
        } else {
          setCameraStatus('failed');
          setCameraError({
            title: tr('Camera not working'),
            detail: tr("The camera was detected but isn't providing video. Try a different camera or restart your browser."),
            canRetry: true,
          });
        }
      }, 1500);
    } catch (err) {
      console.error('Camera test error:', err);
      setCameraStatus('failed');
      setCameraError(decodeMediaError(err, 'Camera'));
    }
  }, []);

  // --- Derived state ---
  const allPassed = micStatus === 'passed' && (!isVideo || cameraStatus === 'passed');
  const anyTesting = micStatus === 'testing' || cameraStatus === 'testing';
  const hasStarted = micStatus !== 'idle' || cameraStatus !== 'idle';

  // --- Start all tests ---
  const startAllTests = useCallback(() => {
    testMicrophone();
    if (isVideo) {
      testCamera();
    }
  }, [testMicrophone, testCamera, isVideo]);

  // --- Handle proceed ---
  const handleProceed = () => {
    // Stop camera stream before proceeding (interview page will request its own)
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    }
    onProceed();
  };

  if (!isOpen) return null;

  // --- Render helpers ---
  const renderStatusIcon = (status) => {
    switch (status) {
      case 'idle':
        return (
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-gray-300" />
          </div>
        );
      case 'testing':
        return (
          <div className="w-10 h-10 rounded-full bg-electric-blue-50 flex items-center justify-center relative">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-electric-blue border-t-transparent" />
          </div>
        );
      case 'passed':
        return (
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
            <CheckCircleSolid className="w-6 h-6 text-emerald-500" />
          </div>
        );
      case 'failed':
        return (
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
            <ExclamationCircleIcon className="w-6 h-6 text-red-500" />
          </div>
        );
      default:
        return null;
    }
  };

  const renderAudioLevelBars = () => {
    const bars = 20;
    return (
      <div className="flex items-end gap-[2px] h-8 mt-3">
        {[...Array(bars)].map((_, i) => {
          const threshold = i / bars;
          const isActive = audioLevel > threshold;
          const height = 8 + (i / bars) * 24;
          return (
            <div
              key={i}
              className="flex-1 rounded-sm transition-all duration-75"
              style={{
                height: `${height}px`,
                backgroundColor: isActive
                  ? i < bars * 0.6
                    ? '#22c55e'
                    : i < bars * 0.85
                    ? '#f59e0b'
                    : '#ef4444'
                  : '#e5e7eb',
                transition: 'background-color 75ms ease',
              }}
            />
          );
        })}
      </div>
    );
  };

  const renderErrorCard = (error, onRetry) => {
    if (!error) return null;
    const platform = error.platform;
    const browserName = platform ? getBrowserDisplayName(platform.browser) : null;
    const deviceName = platform ? getDeviceDisplayName(platform.device, platform.os) : null;

    return (
      <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 sm:p-4 animate-fade-in">
        <div className="flex gap-2.5">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-red-800">{error.title}</p>
              {platform && browserName && (
                <span className="text-[10px] font-medium text-red-600 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                  {deviceName} · {browserName}
                </span>
              )}
            </div>
            <p className="text-xs text-red-700 mt-0.5 leading-relaxed">{error.detail}</p>

            {/* Platform-specific fix steps for permission denied */}
            {error.fixSteps && error.fixSteps.length > 0 && (
              <div className="mt-2.5 text-xs leading-relaxed">
                <p className="font-semibold text-red-800 mb-1.5">
                  {t('How to fix on {deviceName} ({browserName}):', { deviceName, browserName })}
                </p>
                <ol className="list-none space-y-1.5">
                  {error.fixSteps.map((step, idx) => (
                    <li key={idx} className="flex gap-2 items-start">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-200/60 text-red-700 text-[10px] font-bold flex items-center justify-center mt-0.5">
                        {idx + 1}
                      </span>
                      <span className="text-red-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Generic fallback for non-permission errors that are not retryable */}
            {!error.canRetry && !error.fixSteps && (
              <div className="mt-2 text-xs text-red-600 leading-relaxed">
                <p className="font-medium mb-1">{t('How to fix:')}</p>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li>{t('Look for a 🔒 lock icon in the address bar')}</li>
                  <li>{t('Find permission settings for this site')}</li>
                  <li>{t('Allow camera and microphone access')}</li>
                  <li>{t('Refresh this page')}</li>
                </ol>
              </div>
            )}

            {/* Retry button */}
            {error.canRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-red-900 bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                {t('Try again')}
              </button>
            )}

            {/* For permission denied — also show retry after fix */}
            {!error.canRetry && error.fixSteps && (
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <ArrowPathIcon className="w-3.5 h-3.5" />
                  {t('Refresh Page')}
                </button>
                <span className="text-[10px] text-red-600">{t('After changing the permission')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // --- Browser not supported ---
  if (!browserSupported) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center animate-slide-up">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <ShieldExclamationIcon className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('Browser Not Supported')}</h2>
          <p className="text-sm text-gray-600 mb-6">
            {t("Your browser doesn't support camera and microphone access. Please use a modern browser like")}
            <strong> Google Chrome</strong>, <strong>Microsoft Edge</strong>, {t('or')} <strong>Safari</strong>.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('Go Back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
      <div
        className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-slide-up"
        style={{ maxHeight: 'calc(100vh - 32px)' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-electric-blue to-indigo-600 px-5 py-4 sm:px-6 sm:py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/20 flex items-center justify-center">
              {isVideo ? (
                <VideoCameraIcon className="w-5 h-5 text-white" />
              ) : (
                <MicrophoneIcon className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">
                {t('Device Check')}
              </h2>
              <p className="text-[11px] sm:text-xs text-white/80">
                {t("Let's make sure everything works")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            aria-label={t('Close')}
          >
            <XMarkIcon className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {/* Not started state */}
          {!hasStarted && (
            <div className="text-center animate-fade-in">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-electric-blue-50 flex items-center justify-center">
                {isVideo ? (
                  <VideoCameraIcon className="w-10 h-10 text-electric-blue" />
                ) : (
                  <MicrophoneIcon className="w-10 h-10 text-electric-blue" />
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1.5">
                {isVideo ? t('Camera & Microphone Check') : t('Microphone Check')}
              </h3>
              <p className="text-sm text-gray-600 mb-2 max-w-sm mx-auto">
                {isVideo
                  ? t("We'll quickly test your camera and microphone to make sure the interview runs smoothly.")
                  : t("We'll quickly test your microphone to make sure the interview runs smoothly.")}
              </p>
              <p className="text-xs text-gray-500 mb-6">
                {t('Your browser will ask for permission — please click')} <strong>{t('"Allow"')}</strong>.
              </p>

              {/* Permission illustration */}
              <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center gap-3 justify-center">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-7 h-7 rounded-lg bg-electric-blue-50 flex items-center justify-center">
                      <MicrophoneIcon className="w-3.5 h-3.5 text-electric-blue" />
                    </div>
                    <span>{t('Microphone')}</span>
                    <CheckCircleIcon className="w-4 h-4 text-gray-300" />
                  </div>
                  {isVideo && (
                    <>
                      <div className="w-px h-6 bg-gray-300" />
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <div className="w-7 h-7 rounded-lg bg-electric-blue-50 flex items-center justify-center">
                          <VideoCameraIcon className="w-3.5 h-3.5 text-electric-blue" />
                        </div>
                        <span>{t('Camera')}</span>
                        <CheckCircleIcon className="w-4 h-4 text-gray-300" />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={startAllTests}
                className="w-full py-3 bg-gradient-to-r from-electric-blue to-indigo-600 hover:from-electric-blue/90 hover:to-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-electric-blue/20 hover:shadow-xl transition-all text-sm sm:text-base"
              >
                {t('Start Device Check')}
              </button>
            </div>
          )}

          {/* Testing in progress or completed */}
          {hasStarted && (
            <div className="space-y-4 animate-fade-in">
              {/* Microphone test card */}
              <div className={`rounded-xl border p-4 transition-all duration-300 ${
                micStatus === 'passed'
                  ? 'border-emerald-200 bg-emerald-50/50'
                  : micStatus === 'failed'
                  ? 'border-red-200 bg-red-50/30'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="flex items-start gap-3">
                  {renderStatusIcon(micStatus)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-gray-900">{t('Microphone')}</h4>
                      {micStatus === 'testing' && (
                        <span className="text-[10px] font-medium text-electric-blue bg-electric-blue-50 px-2 py-0.5 rounded-full">
                          {isRecording ? t('Recording...') : isPlaying ? t('Playing back...') : t('Listening...')}
                        </span>
                      )}
                      {micStatus === 'passed' && (
                        <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                          {t('Working great!')}
                        </span>
                      )}
                    </div>

                    {/* Testing state — show audio bars */}
                    {micStatus === 'testing' && !isRecording && !isPlaying && (
                      <div className="mt-1">
                        <p className="text-xs text-gray-500">
                          {peakDetected 
                            ? t('✓ Audio detected! Hold on...')
                            : t('Speak or make a sound to test...')}
                        </p>
                        {renderAudioLevelBars()}
                      </div>
                    )}

                    {/* Recording state */}
                    {micStatus === 'testing' && isRecording && (
                      <div className="mt-1">
                        <p className="text-xs text-gray-500">{t('Recording a short clip...')}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-xs font-medium text-red-600">REC</span>
                        </div>
                      </div>
                    )}

                    {/* Playing back */}
                    {micStatus === 'testing' && isPlaying && (
                      <div className="mt-1">
                        <p className="text-xs text-gray-500">
                          {t('Playing back your audio — can you hear yourself?')}
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <SpeakerWaveIcon className="w-4 h-4 text-electric-blue animate-pulse" />
                          <span className="text-xs font-medium text-electric-blue">{t('Playing...')}</span>
                        </div>
                      </div>
                    )}

                    {/* Passed */}
                    {micStatus === 'passed' && (
                      <p className="text-xs text-emerald-700 mt-0.5">
                        {t('Your microphone is working perfectly.')}
                      </p>
                    )}

                    {/* Error */}
                    {renderErrorCard(micError, testMicrophone)}
                  </div>
                </div>
              </div>

              {/* Camera test card (video interviews only) */}
              {isVideo && (
                <div className={`rounded-xl border p-4 transition-all duration-300 ${
                  cameraStatus === 'passed'
                    ? 'border-emerald-200 bg-emerald-50/50'
                    : cameraStatus === 'failed'
                    ? 'border-red-200 bg-red-50/30'
                    : 'border-gray-200 bg-white'
                }`}>
                  <div className="flex items-start gap-3">
                    {renderStatusIcon(cameraStatus)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-gray-900">{t('Camera')}</h4>
                        {cameraStatus === 'testing' && (
                          <span className="text-[10px] font-medium text-electric-blue bg-electric-blue-50 px-2 py-0.5 rounded-full">
                            {t('Checking...')}
                          </span>
                        )}
                        {cameraStatus === 'passed' && (
                          <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                            {t('Looking good!')}
                          </span>
                        )}
                      </div>

                      {/* Camera preview */}
                      {(cameraStatus === 'testing' || cameraStatus === 'passed') && (
                        <div className="mt-3 rounded-lg overflow-hidden bg-gray-900 aspect-video relative">
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                            style={{ transform: 'scaleX(-1)' }}
                          />
                          {cameraStatus === 'passed' && (
                            <div className="absolute top-2 right-2 flex items-center gap-1 bg-emerald-500/90 text-white text-[10px] font-semibold rounded-full px-2 py-0.5">
                              <CheckCircleSolid className="w-3 h-3" />
                              {t('Ready')}
                            </div>
                          )}
                        </div>
                      )}

                      {/* E2E-only bypass, same reasoning as ScreeningApply.js's
                          e2e-force-submit-btn: a headless browser's fake camera can't
                          be made to pass a "well lit and centered" real check either.
                          Was previously gated on NODE_ENV === 'development', which
                          (since CRA's build always sets NODE_ENV=production for every
                          deployed build, dev or prod — see isProdHost.js) meant this
                          never rendered on ANY deployed environment, including dev.
                          Hostname-gated instead so it works on dev but never on prod. */}
                      {!isProdHost() && (
                        <button
                          type="button"
                          data-testid="e2e-bypass-device-check"
                          onClick={handleProceed}
                          className="hidden"
                        >
                          Bypass
                        </button>
                      )}

                      {/* Passed text */}
                      {cameraStatus === 'passed' && (
                        <p className="text-xs text-emerald-700 mt-2">
                          {t("Camera is working. Make sure you're well lit and centered.")}
                        </p>
                      )}

                      {/* Error */}
                      {renderErrorCard(cameraError, testCamera)}
                    </div>
                  </div>
                </div>
              )}

              {/* All passed summary */}
              {allPassed && (
                <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-4 animate-bounce-in">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircleSolid className="w-6 h-6 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-emerald-900">{t('All checks passed!')}</p>
                      <p className="text-xs text-emerald-700">
                        {t("You're all set. Click below to start your interview.")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 sm:py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors text-sm"
                >
                  {t('Cancel')}
                </button>
                {allPassed ? (
                  <button
                    type="button"
                    data-testid="device-check-start-btn"
                    onClick={handleProceed}
                    className="flex-[2] py-2.5 sm:py-3 bg-gradient-to-r from-electric-blue to-indigo-600 hover:from-electric-blue/90 hover:to-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-electric-blue/20 hover:shadow-xl transition-all text-sm sm:text-base flex items-center justify-center gap-2"
                  >
                    {t('Start Interview')}
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={anyTesting}
                    onClick={startAllTests}
                    className="flex-[2] py-2.5 sm:py-3 bg-gradient-to-r from-electric-blue to-indigo-600 hover:from-electric-blue/90 hover:to-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-electric-blue/20 hover:shadow-xl transition-all text-sm sm:text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {anyTesting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        {t('Testing...')}
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="w-4 h-4" />
                        {t('Retry Tests')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceCheckModal;
